import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { GET_SPECIAL_PACK_EDITION_OPTIONS } from "../../../graphql/resolvers/specialPack";
import { showErrorAlert } from "../../../helpers/alerts";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { logError } from "../../../helpers/logging";
import {
  SpecialPackEditionOptions,
  SpecialPackEditionOptionsVariables,
} from "../../../types/__generated__/SpecialPackEditionOptions";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import OffCanvasTray from "../OffCanvasTray";
import SpecialPackAddToCartTray from "../SpecialPackAddToCartTray";
import { CloseButton, StyledBoxTray, Wrapper } from "../SpecialPackAddToCartTray/style";

interface SpecialPackProductAddTrayProps {
  editionSlug: string;
  // Option product whose ADD was clicked; the tray opens with it at quantity 1
  preselectedProductId?: number | null;
  onClose: () => void;
}

/**
 * Opens the wine special packs' add-to-cart tray from a pack option card.
 * The product listings only carry the option product, so the edition and its
 * options are fetched here first, inside the same tray shell, before the shared
 * tray takes over.
 */
const SpecialPackProductAddTray = ({
  editionSlug,
  preselectedProductId = null,
  onClose,
}: SpecialPackProductAddTrayProps) => {
  const { t } = useTranslation(I18N_NAMESPACES.COMMON);
  const { data, loading, error } = useQuery<SpecialPackEditionOptions, SpecialPackEditionOptionsVariables>(
    GET_SPECIAL_PACK_EDITION_OPTIONS,
    { variables: { slug: editionSlug } }
  );
  const specialPackEdition = data?.specialPackEdition;
  const hasFailed = !loading && (!!error || !specialPackEdition);

  useEffect(() => {
    if (!hasFailed) return;
    if (error) {
      logError(error, { location: "SpecialPackProductAddTray", action: "GET_SPECIAL_PACK_EDITION_OPTIONS" });
    }
    showErrorAlert(t("common:sorryTryAgain"));
    onClose();
  }, [hasFailed, error, onClose, t]);

  if (!specialPackEdition) {
    return (
      <OffCanvasTray setOverflowHidden={false} isOffcanvasOpened outSideClickHandler={onClose}>
        <StyledBoxTray isOpened>
          <Wrapper>
            <CloseButton onClick={onClose} />
            <StyledLoading />
          </Wrapper>
        </StyledBoxTray>
      </OffCanvasTray>
    );
  }

  return (
    <SpecialPackAddToCartTray
      specialPackEdition={specialPackEdition}
      preselectedProductId={preselectedProductId}
      onClose={onClose}
      closeTrays={onClose}
    />
  );
};

export default SpecialPackProductAddTray;
